import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import TextInput from '../inputs/TextInput';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { FaTimes } from "react-icons/fa";
import Autocomplete from '../inputs/AutoComplete';
import { modifyCommittee } from '../../api/admin/AdminDashboardAPI';
import _ from 'lodash';
import toast from 'react-hot-toast';

Modal.setAppElement('#root');

const CommitteeEditModal = ({ isOpen, onClose, committee, initialFormData, onFormDataChange, members, updateCommittee }) => {
    const headsFromCommittee = (committee?.heads || [])
        .map(m => `${m.member.firstName} ${m.member.lastName} (${m.member.ritUserID})`)
        .join(', ');

    // Initialize state from initialFormData if provided; otherwise use committee defaults.
    const [name, setName] = useState(initialFormData?.name || committee?.committeeName || "");
    const [heads, setHeads] = useState(
        initialFormData?.heads || headsFromCommittee || ""
    );

    const suggestions = members.map(member =>
        `${member.firstName} ${member.lastName} (${member.ritUserID})`
    );

    // Ref to ensure we initialize state only once per modal open.
    const didInit = useRef(false);

    // Ref to hold a stable version of onFormDataChange.
    const onFormDataChangeRef = useRef(onFormDataChange);
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    // Initialize (or reinitialize) state when modal opens.
    useEffect(() => {
        if (isOpen) {
            if (!didInit.current && committee) {
                // If initialFormData is provided and non-empty, use that.
                if (initialFormData && Object.keys(initialFormData).length > 0 && (initialFormData.name !== "" || initialFormData.heads != "")) {
                    setName(initialFormData.name || "");
                    setHeads(initialFormData.heads || "");
                } else {
                    setName(committee.committeeName || "");
                    setHeads(headsFromCommittee || "");
                }
                didInit.current = true;
            }
        } else {
            // Reset the initialization flag when modal closes.
            didInit.current = false;
        }
    }, [isOpen, committee, initialFormData]);

    // Propagate form data changes to the parent.
    useEffect(() => {
        if (onFormDataChangeRef.current) {
            onFormDataChangeRef.current({ name, heads });
        }
    }, [name, heads]);

    // Reset handler: revert form fields to committee defaults.
    const handleReset = () => {
        if (committee) {
            setName(committee.committeeName || "");
            setHeads(headsFromCommittee || "");
        }
    };

    const getMatchingHeads = () => {
        const headNames = heads
            .split(',')
            // remove any “(…)” and then trim
            .map(name =>
                name
                    .replace(/\s*\([^)]*\)\s*/g, '')  // ← kill anything in parentheses (and the extra spaces)
                    .trim()
            )
            // throw away any empty strings
            .filter(name => name.length > 0);

        const nameToMemberMap = new Map(
            members.map(member => [
                `${member.firstName} ${member.lastName}`.toLowerCase(),
                member
            ])
        );

        return headNames
            .map(name => nameToMemberMap.get(name.toLowerCase()))
            .filter(Boolean);
    };

    const updatedCommittee = {
        ...committee,
        committeeName: name,
        heads: getMatchingHeads().map(member => ({ member }))
    }

    const submitCommittee = async () => {
        const isUnchanged = _.isEqual(committee, updatedCommittee);

        if (isUnchanged) {
            toast.error("No data changed");
            return;
        }

        const rawHeadNames = heads
            .split(',')
            .map(n =>
                // remove any “(… )” plus surrounding spaces, then trim
                n.replace(/\s*\([^)]*\)\s*/g, '').trim()
            )
            .filter(n => n.length > 0);

        const nameToMemberMap = new Map(
            members.map(m => [
                `${m.firstName} ${m.lastName}`.toLowerCase(),
                m
            ])
        );

        const seen = new Set();
        const duplicates = rawHeadNames.filter(n => {
            const key = n.toLowerCase();
            if (seen.has(key)) return true;
            seen.add(key);
            return false;
        });
        if (duplicates.length) {
            toast.error(
                `Duplicate head name${duplicates.length > 1 ? 's' : ''}: ${[...new Set(duplicates)].join(', ')}`
            );
            return;
        }

        const notFound = rawHeadNames.filter(n => !nameToMemberMap.has(n.toLowerCase()));
        if (notFound.length) {
            toast.error(
                `Head${notFound.length > 1 ? 's' : ''} not found: ${notFound.join(', ')}`
            );
            return;
        }

        const isCorrectName = name != "" && name != null;

        if (!isCorrectName) {
            toast.error("Committee name must be filled out");
            return;
        }

        if (updatedCommittee.committeeName.length > 80) {
            toast.error("Committee name is too long");
            return;
        }

        try {
            toast.loading("Updating Committee...");
            setTimeout(async () => {
                console.log("Payload to update:", updatedCommittee);
                const response = await modifyCommittee(updatedCommittee);
                if (response.ok) {
                    toast.dismiss();
                    didInit.current = false;
                    updateCommittee(updatedCommittee);
                    toast.success("Committee updated!");
                    onClose();
                } else {
                    toast.dismiss();
                    console.log("Error updating committee. Status:", response.status);
                    toast.error("Committee failed to update. Status: " + response.status);
                }
            }, 300);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting committee:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Committee Edit Modal"
            className="custom-modal-content"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className="d-flex flex-row justify-content-between align-items-center mb-3">
                    <h2 className="text-start mb-0">Edit Committee</h2>
                    <div className="d-flex flex-row justify-content-end align-items-center me-1">
                        <button className="close-btn" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
                <div className="form-field w-75">
                    <TextInput
                        id="committeeNameEdit"
                        placeholder="Committee Name"
                        className="form-input"
                        charLimit={200}
                        label="Committee Name"
                        value={name}
                        setValue={(e) => setName(e.target.value)}
                        required
                    />
                </div>
                <div className="form-field mt-3 w-75">
                    <Autocomplete
                        suggestions={suggestions}
                        value={heads}
                        label={"Committee Head(s)"}
                        onChange={setHeads}
                    />
                </div>
                <div className="d-flex flex-row justify-content-center align-items-center two-button-container mt-3">
                    <div className="me-3">
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Save"
                        action={submitCommittee}
                    />
                </div>
            </>
        </Modal>
    );
};

CommitteeEditModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    committee: PropTypes.object,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
};

export default CommitteeEditModal;
